import { useMemo, useState } from 'react'
import { Trans } from 'react-i18next'
import { useBoolean } from 'react-use'
import clsx from 'clsx'

import { IActionType, VariableTextFeatureFlag } from '@makyoapp/makyo-common'
import { ChevronLeft, ChevronRight, Search16 } from '@makyoapp/makyo-icon/icon'
import { ColumnTypes, MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { cn, DDItem, SimpleInput } from '@makyoapp/makyo-ui'

import { ActionDataType, ActionInputdata, NodeOutputOption } from '@/shared/action.types'
import { SHOW_SEARCH_LIMIT } from '@/shared/constants'
import { appIconKeys, ColumnIconMap } from '@/shared/icons'
import { Option } from '@/shared/types'

import { VariableWrapper } from './VariableWrapper'

type NavigationLevel = {
  options?: NodeOutputOption[]
  parentLabel?: string
  parentId?: string
}

// TODO (ACTION-WORKFLOWS): REFACTOR THIS
// Render each icons for each column type correctly

export default function VariableActionOutput({
  id,
  value,
  onChange,
  onHide,
  options,
}: {
  id?: string
  value?: ActionInputdata
  onChange: (value: {
    id: string
    actionId: string
    flag: VariableTextFeatureFlag.ActionOutput
    value: NodeOutputOption['value']
    dataType: ActionDataType
    actionType: IActionType.TemporaryVariable | IActionType.FilterData
  }) => void
  onHide: () => void
  options?: NodeOutputOption[]
}) {
  const [show, setShow] = useBoolean(false)
  const [search, setSearch] = useState('')
  const [navigationStack, setNavigationStack] = useState<NavigationLevel[]>([])

  // Get current level options - either root options or nested options
  const currentLevel = navigationStack.length > 0 ? navigationStack[navigationStack.length - 1] : null
  const currentOptions = useMemo(() => currentLevel?.options || options || [], [currentLevel, options])

  const filteredActions = useMemo(() => {
    if (search) {
      const searchValue = search.toLowerCase()
      return currentOptions.filter(
        (node) =>
          node.label.toLowerCase().includes(searchValue) || node.description?.toLowerCase().includes(searchValue)
      )
    }
    return currentOptions
  }, [currentOptions, search])

  const showSearch = currentOptions && currentOptions.length > SHOW_SEARCH_LIMIT

  // Navigate to nested options
  const navigateToNested = (parentOption: Option) => {
    if (parentOption.options && parentOption.options.length > 0) {
      setNavigationStack((prev) => [
        ...prev,
        {
          options: parentOption.options!,
          parentLabel: parentOption.label,
          parentId: parentOption.id,
        },
      ])
      setSearch('') // Clear search when navigating
    }
  }

  // Navigate back to parent level
  const navigateBack = () => {
    setNavigationStack((prev) => prev.slice(0, -1))
    setSearch('') // Clear search when navigating back
  }

  // Handle selection - works for both parent and nested items
  const handleSelection = (option: NodeOutputOption) => {
    // If this option has nested options, navigate to them
    if (option.options && option.options.length > 0) {
      navigateToNested(option)
      return
    }
    // Otherwise, select this option
    const valueToSend = option.value || option.id
    onChange({
      id: option.id,
      flag: VariableTextFeatureFlag.ActionOutput,
      dataType: ActionDataType.ActionOutput,
      value: valueToSend,
      actionType: option.actionType as IActionType.TemporaryVariable | IActionType.FilterData,
      actionId: option.id,
    })
    onHide()
  }

  // Reset navigation when dropdown is closed
  const handleSetShow = (value: boolean | ((prevState: boolean) => boolean)) => {
    const newValue = typeof value === 'function' ? value(show) : value
    setShow(newValue)
    if (!newValue) {
      setNavigationStack([])
      setSearch('')
    }
  }

  return (
    <VariableWrapper
      title={<Trans>Node Output</Trans>}
      icon={<MaterialIcon size={16} icon={appIconKeys.actionOutput} />}
      show={show}
      defaultHeight={false}
      defaultWidth={false}
      setShow={handleSetShow}
      placement='left'
      defaultMenuPad={false}
      menuClassName={clsx('w-[250px] min-h-[170px]')}
    >
      <div>
        {/* Navigation Header */}
        {currentLevel && (
          <div className='text-m-m flex items-center justify-between gap-2 px-3 py-2 border-b border-zinc-200 dark:border-slate-600 bg-zinc-50 dark:bg-slate-700'>
            <button
              onClick={navigateBack}
              className='flex items-center gap-1 text-zinc-600 dark:text-slate-300 hover:text-zinc-800 dark:hover:text-slate-100'
            >
              <ChevronLeft width={11} height={11} />
              <Trans>Back</Trans>
            </button>
            <div className='text-light font-mediumtruncate'>{currentLevel.parentLabel}</div>
          </div>
        )}

        {showSearch && (
          <div className='flex border-b border-zinc-200 dark:border-slate-600'>
            <SimpleInput
              noBorder
              outlined
              className='mk-search-box pr-3!'
              placeholder='Search...'
              value={search}
              onChange={(e) => {
                setSearch(e.currentTarget.value)
              }}
              showClear={!!search}
              onClear={() => {
                setSearch('')
              }}
              before={<Search16 className='mk-search-box-icon' />}
            />
          </div>
        )}

        <div className={cn('py-2 max-h-[200px] overflow-y-auto')}>
          {!filteredActions?.length ? (
            <div className='py-2 px-4 text-light'>
              <Trans>No nodes found</Trans>
            </div>
          ) : (
            filteredActions.map((item) => {
              const hasNestedOptions = item.options && item.options.length > 0

              const id = typeof item.value === 'object' ? (item.value?.colId ?? item.value?.actionId) : item.id

              return (
                <DDItem
                  key={id}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleSelection(item)
                  }}
                  className={cn('w-full cursor-pointer hover:bg-zinc-100 dark:hover:bg-slate-700')}
                >
                  <div className='flex items-center justify-between w-full'>
                    <div className='flex flex-col flex-1'>
                      <div className='flex items-center gap-2'>
                        {item.optionsType === 'column' && typeof item.value === 'object' && 'colType' in item.value && (
                          <MaterialIcon size={16} icon={ColumnIconMap[item.value.colType as ColumnTypes]} />
                        )}
                        <span>{item.label}</span>
                      </div>
                      {item.description && <div className='text-xs text-light mt-1'>{item.description}</div>}
                    </div>
                    {hasNestedOptions && (
                      <ChevronRight width={14} height={14} className='text-zinc-400 dark:text-slate-500 ml-2' />
                    )}
                  </div>
                </DDItem>
              )
            })
          )}
        </div>
      </div>
    </VariableWrapper>
  )
}
